/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useEffect, useContext } from "react";
import customHttp from "../services/customHTTP";
import { useSite } from "./siteContext";
import { useAuth } from "./authContext";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
	const [cartItems, setCartItems] = useState([]);
	const [cartCount, setCartCount] = useState(0);
	const [userId, setUserId] = useState(null); // Set initial state to null
	const { selectedSite } = useSite();
	const { user } = useAuth();

	const updateCart = async (cartData) => {
		const totalQuantity = cartData
			.filter((item) => item.Site === selectedSite) // Filter by selectedSite
			.reduce((total, item) => total + item.Quantity, 0);
		setCartItems(cartData);
		setCartCount(totalQuantity);
	};

	// Function to fetch the cart items
	const fetchCart = async (userId) => {
		if (!userId) return;
		try {
			const response = await customHttp.post("/cart/getCart", { userId });
			await updateCart(response.data);
		} catch (error) {
			console.error("Error fetching cart:", error);
		}
	};

	// UseEffect to initialize userId from sessionStorage
	useEffect(() => {
		if (user) {
			setUserId(user.user_id);
		}
	}, []); // Runs only on mount

	// Fetch cart if userId is set (can also be manually triggered after login)
	useEffect(() => {
		if (userId) {
			fetchCart(userId); // Fetch the cart immediately when userId is available
		}
	}, [userId, selectedSite]); // Runs when userId changes

	useEffect(() => {
		if (user) {
			setUserId(user.user_id);
		}
	}, [user]);

	// Add to cart
	const addToCart = async (product) => {
		try {
			if (userId) {
				// Check if the product already exists in the cart
				const existingProduct = cartItems.find((item) => {
					return item.ProductID === product.ProductID && item.Site === selectedSite;
				});

				if (existingProduct) {
					// If product exists, update the quantity
					const updatedCart = cartItems.map((item) =>
						item.ProductID === product.ProductID
							? { ...item, Quantity: item.Quantity + product.Quantity }
							: item
					);
					await customHttp.post("/cart", { userId, cart: updatedCart });
					await updateCart(updatedCart);
				} else {
					// If product doesn't exist, add it to the cart
					const updatedCart = [...cartItems, product];
					await customHttp.post("/cart", { userId, cart: updatedCart });
					await updateCart(updatedCart);
				}
			}
		} catch (error) {
			console.error("Error adding to cart:", error);
		}
	};

	const addMultipleToCart = async (products) => {
		try {
			if (userId) {
				// Create a copy of the cartItems to modify
				let updatedCart = [...cartItems];

				// Iterate over the products to add/update them in the cart
				products.forEach((product) => {
					const existingProduct = updatedCart.find(
						(item) => item.ProductID === product.ProductID && item.Site === product.Site
					);

					if (existingProduct) {
						// If the product exists, update its quantity
						existingProduct.Quantity += product.Quantity;
					} else {
						// If the product doesn't exist, add it to the cart
						updatedCart.push(product);
					}
				});

				// Save the updated cart
				await customHttp.post("/cart", { userId, cart: updatedCart });
				await updateCart(updatedCart);
			}
		} catch (error) {
			console.error("Error adding to cart:", error);
		}
	};

	// Remove from cart
	const removeFromCart = async (productId) => {
		try {
			if (userId) {
				const updatedCart = cartItems.filter(
					(item) => !(item.ProductID === productId && item.Site === selectedSite)
				);

				await customHttp.post("/cart", { userId, cart: updatedCart });
				await updateCart(updatedCart);
			}
		} catch (error) {
			console.error("Error removing from cart:", error);
		}
	};

	// Clear cart function
	const clearCart = async () => {
		try {
			if (userId) {
				// Filter out items that match the selectedSite
				const updatedCart = cartItems.filter((item) => item.Site !== selectedSite);
				await customHttp.post("/cart", { userId, cart: updatedCart });
				await updateCart(updatedCart);
			}
		} catch (error) {
			console.error("Error clearing the cart:", error);
		}
	};

	return (
		<CartContext.Provider
			value={{ cartItems, cartCount, addToCart, removeFromCart, clearCart, updateCart, addMultipleToCart }}>
			{children}
		</CartContext.Provider>
	);
};

export const useCart = () => useContext(CartContext);
